/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, forwardRef, useRef, useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './ContractAndOffer.style';
import { useIntl } from 'react-intl';
import VoucherBuilder from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/VoucherBuilder';
import TenderSharedContractPrint from '~components/molecules/RightSideView/components/TenderContractsPrint/TenderSharedContractPrint';
import TenderContractPrint from '~components/molecules/RightSideView/components/TenderContractsPrint/TenderContractPrint';
import {
  isTenderClosed,
  useTenderVendorSubHeader,
} from '~src/utils/isTenderClosed';
import { useDynamicHeightAndLockScroll } from '~src/hooks/useDynamicHeightAndLockScroll';

export type OfferBaseMetadata = {
  projectId?: number;
  tenderId?: string;
  shareKey?: string;
  shareId?: string;
  isStartFetch?: boolean;
  tenderRegData: any;
};

export const ContractAndOffer = forwardRef<HTMLDivElement, OfferBaseMetadata>(
  (
    { tenderId, projectId, shareId, shareKey, isStartFetch, tenderRegData },
    ref
  ) => {
    const styles = useStyles(styleFn);
    const intl = useIntl();
    const isOnMyTender = shareId && shareKey;
    const tenderClosed = isTenderClosed(tenderRegData);

    const vendorSubHeader = useTenderVendorSubHeader(tenderRegData);
    const {
      containerRef,
      dynamicHeight,
    } = useDynamicHeightAndLockScroll<HTMLDivElement>();

    return (
      <Fragment>
        <div
          ref={containerRef}
          css={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            height: dynamicHeight,
            minHeight: '400px',
          }}>
          <Row css={{ height: '100%', minHeight: 0 }}>
            <Col
              span={22}
              css={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
              }}>
              <div
                css={{
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  paddingTop: '20px',
                  paddingLeft: '10px',
                  paddingBottom: '20px',
                }}>
                <h2>{intl.formatMessage({ id: 'tab-contracts' })}</h2>

                <div css={{ color: 'grey', whiteSpace: 'pre-wrap' }}>
                  {isOnMyTender
                    ? intl.formatMessage({
                        id: 'tab-vendors-contract-description',
                      })
                    : intl.formatMessage({
                        id: 'tab-project-contract-description',
                      })}
                </div>

                {isOnMyTender && vendorSubHeader && (
                  <div
                    css={{
                      color: vendorSubHeader.color,
                      fontSize: '13px',
                      lineHeight: 1.35,
                      maxWidth: '900px',
                    }}>
                    {vendorSubHeader.text}
                  </div>
                )}

                {!isOnMyTender && tenderClosed && (
                  <div
                    css={{
                      borderRadius: '4px',
                      color: '#cf1322',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      marginTop: '10px',
                    }}>
                    <strong
                      css={{
                        display: 'block',
                        marginBottom: '4px',
                        whiteSpace: 'pre-wrap',
                      }}>
                      {intl.formatMessage({
                        id: 'tender-closed-contracts-readonly-title',
                      })}
                    </strong>
                    <span
                      css={{
                        whiteSpace: 'pre-wrap',
                      }}>
                      {intl.formatMessage({
                        id: 'tender-closed-contracts-readonly-subtitle',
                      })}
                    </span>
                  </div>
                )}
              </div>

              <div
                css={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  padding: '25px 25px 5px 25px', // low tier padding '5px'
                }}>
                <VoucherBuilder
                  projectId={projectId}
                  tenderId={tenderId}
                  shareId={shareId}
                  shareKey={shareKey}
                  tenderRegData={tenderRegData}
                />
              </div>
            </Col>
          </Row>

          {/* PRINT ONLY CONTAINER */}
          <div
            css={{
              position: 'absolute',
              zIndex: -1,
              opacity: 0,
            }}>
            {isOnMyTender ? (
              <TenderSharedContractPrint
                tenderId={tenderId ?? ''}
                shareId={shareId}
                shareKey={shareKey}
                isStartFetch={true}
                isExpandable
                ref={ref}
              />
            ) : projectId && tenderId ? (
              <TenderContractPrint
                projectId={String(projectId) ?? ''}
                tenderId={tenderId ?? ''}
                isStartFetch={true}
                isExpandable
                ref={ref}
              />
            ) : null}
          </div>
        </div>
      </Fragment>
    );
  }
);

export default ContractAndOffer;
